import React, { memo, useMemo, useCallback } from "react";
import { Cap, Level } from "../../../utils/rbacCatalog";
import { useTranslation } from "react-i18next";
import {
  HiOutlinePencil,
  HiOutlineShare,
  HiOutlineTrash,
} from "react-icons/hi";
import { HiTicket } from "react-icons/hi2";
import { EventListItem } from "../../../hooks/useEventPayments";
import useAuth from "../../../hooks/useAuth";
import { usePermissions } from "../../../utils/permissions";
import { CustomButton } from "../../atoms/CustomButton";
import { StatusBadge } from "../../atoms/StatusBadge";
import TicketPreview from "./TicketPreview";
import MediaCard from "../MediaCard";

interface EventCardProps {
  event: EventListItem;
  onShare: (code: string) => void;
  onEdit: (code: string) => void;
  onDelete: (code: string) => void;
}

const MAX_VISIBLE_TICKETS = 2;

const EventCard: React.FC<EventCardProps> = ({
  event,
  onShare,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const permissions = usePermissions(user);
  const canWrite = permissions.can(Cap.EVENTS, Level.WRITE);

  const totalSold = useMemo(
    () =>
      event.ticket_types?.reduce(
        (sum, ticket) => sum + (ticket.quantity_sold || 0),
        0
      ) ?? 0,
    [event.ticket_types]
  );

  const visibleTickets = useMemo(
    () => event.ticket_types?.slice(0, MAX_VISIBLE_TICKETS) ?? [],
    [event.ticket_types]
  );

  const remainingTicketCount = useMemo(
    () => Math.max(0, (event.ticket_types?.length ?? 0) - MAX_VISIBLE_TICKETS),
    [event.ticket_types]
  );

  const handleShare = useCallback(
    () => onShare(event.code),
    [onShare, event.code]
  );
  const handleEdit = useCallback(
    () => onEdit(event.code),
    [onEdit, event.code]
  );
  const handleDelete = useCallback(
    () => onDelete(event.code),
    [onDelete, event.code]
  );

  const statusBadge = event.active ? (
    <StatusBadge color="success" dot>
      {t("components.eventPayments.eventCard.active")}
    </StatusBadge>
  ) : (
    <StatusBadge color="gray" dot>
      {t("components.eventPayments.eventCard.inactive")}
    </StatusBadge>
  );

  const actions = (
    <>
      <CustomButton
        onClick={handleShare}
        variant="secondary"
        size="sm"
        aria-label={`${t("components.eventPayments.actions.share")} ${
          event.event_name
        }`}
        comp={
          <>
            <HiOutlineShare size={14} aria-hidden="true" />
            <span className="hidden sm:inline">
              {t("components.eventPayments.actions.share")}
            </span>
          </>
        }
      />
      {canWrite && (
        <CustomButton
          onClick={handleEdit}
          variant="secondary"
          size="sm"
          aria-label={`${t("common.edit")} ${event.event_name}`}
          comp={
            <>
              <HiOutlinePencil size={14} aria-hidden="true" />
              <span className="hidden sm:inline">{t("common.edit")}</span>
            </>
          }
        />
      )}
      {permissions.can(Cap.EVENTS, Level.DELETE) && (
        <CustomButton
          onClick={handleDelete}
          variant="secondary"
          size="sm"
          className="text-red-600"
          aria-label={`${t("common.remove")} ${event.event_name}`}
          comp={
            <>
              <HiOutlineTrash size={14} aria-hidden="true" />
              <span className="hidden sm:inline">{t("common.remove")}</span>
            </>
          }
        />
      )}
    </>
  );

  return (
    <MediaCard
      imageUrl={event.event_image_url}
      imageAlt={event.event_name}
      placeholderIcon={
        <HiTicket className="text-gray-300" size={48} aria-hidden="true" />
      }
      badge={statusBadge}
      actions={actions}
    >
      <div className="min-w-0">
        {canWrite ? (
          <button
            type="button"
            onClick={handleEdit}
            title={event.event_name}
            className="block max-w-full truncate text-left text-sm font-semibold text-gray-900 transition-colors hover:text-blue-700"
          >
            {event.event_name}
          </button>
        ) : (
          <div
            className="truncate text-sm font-semibold text-gray-900"
            title={event.event_name}
          >
            {event.event_name}
          </div>
        )}

        {event.event_description && (
          <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">
            {event.event_description}
          </p>
        )}
      </div>

      {event.ticket_types && (
        <dl className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 text-xs">
          <dt className="text-gray-500">
            {t("components.eventPayments.eventCard.ticketsSoldLabel")}
          </dt>
          <dd className="tabular-nums text-gray-700">{totalSold}</dd>
        </dl>
      )}

      <div className="flex flex-col gap-2">
        {visibleTickets.map((ticket) => (
          <TicketPreview key={ticket.ticket_id} ticket={ticket} />
        ))}
        {remainingTicketCount > 0 && (
          <div className="text-xs text-gray-500">
            {t("components.eventPayments.eventCard.moreTicketTypes", {
              count: remainingTicketCount,
            })}
          </div>
        )}
      </div>
    </MediaCard>
  );
};

export default memo(EventCard);
